(() => {
  'use strict';

  const STORAGE_KEY = 'selected_display_currency';
  const BASE_CURRENCY = 'INR';
  const PRICE_SELECTOR = '[data-base-price-cents]';
  const SELECTOR = '[data-display-currency-select]';

  // One INR multiplied by `rate`. Update static display rates only here.
  const CURRENCIES = Object.freeze({
    INR: { rate: 1, symbol: '\u20B9', decimals: 2 },
    USD: { rate: 0.0114, symbol: '$', decimals: 2 },
    GBP: { rate: 0.00855, symbol: '\u00A3', decimals: 2 },
    EUR: { rate: 0.00976, symbol: '\u20AC', decimals: 2 },
    AED: { rate: 0.0419, symbol: '\u062F.\u0625', decimals: 2 },
    CAD: { rate: 0.0157, symbol: 'C$', decimals: 2 },
    AUD: { rate: 0.0175, symbol: 'A$', decimals: 2 },
    SGD: { rate: 0.0147, symbol: 'S$', decimals: 2 },
    JPY: { rate: 1.68, symbol: '\u00A5', decimals: 0 },
    SAR: { rate: 0.04275, symbol: '\u0631.\u0633', decimals: 2 },
    QAR: { rate: 0.0415, symbol: '\u0631.\u0642', decimals: 2 },
    KWD: { rate: 0.0035, symbol: '\u062F.\u0643', decimals: 3 }
  });

  let selectedCurrency = getSelectedCurrency();
  let observerStarted = false;
  let debounceTimer = null;

  function getSelectedCurrency() {
    try {
      const savedCurrency = localStorage.getItem(STORAGE_KEY);
      return CURRENCIES[savedCurrency] ? savedCurrency : BASE_CURRENCY;
    } catch (_) {
      return BASE_CURRENCY;
    }
  }

  function setSelectedCurrency(currencyCode) {
    if (!CURRENCIES[currencyCode]) return;
    selectedCurrency = currencyCode;
    try { localStorage.setItem(STORAGE_KEY, currencyCode); } catch (_) {}
    applyCurrencyConversion();
    document.dispatchEvent(new CustomEvent('display-currency:change', { detail: { currency: currencyCode } }));
  }

  function formatMoney(basePriceCents, currencyCode) {
    const currency = CURRENCIES[currencyCode];
    const convertedAmount = (Number(basePriceCents) / 100) * currency.rate;
    const amount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: currency.decimals,
      maximumFractionDigits: currency.decimals
    }).format(convertedAmount);
    return `${currency.symbol}${amount}${currencyCode === BASE_CURRENCY ? '' : ` ${currencyCode}`}`;
  }

  function getPriceNodes(root) {
    const nodes = [];
    if (root instanceof Element && root.matches(PRICE_SELECTOR)) nodes.push(root);
    if (root?.querySelectorAll) nodes.push(...root.querySelectorAll(PRICE_SELECTOR));
    return nodes;
  }

  function applyCurrencyConversion(root = document) {
    getPriceNodes(root).forEach((priceElement) => {
      const basePriceCents = Number(priceElement.dataset.basePriceCents);
      if (!Number.isFinite(basePriceCents)) return;

      if (!priceElement.dataset.basePriceOriginal) {
        priceElement.dataset.basePriceOriginal = priceElement.textContent.trim();
      }

      const displayValue = selectedCurrency === BASE_CURRENCY
        ? priceElement.dataset.basePriceOriginal
        : formatMoney(basePriceCents, selectedCurrency);

      if (displayValue && priceElement.textContent.trim() !== displayValue) {
        priceElement.textContent = displayValue;
      }
    });

    document.querySelectorAll(SELECTOR).forEach((selector) => {
      if (selector.value !== selectedCurrency) selector.value = selectedCurrency;
    });
    document.documentElement.dataset.displayCurrency = selectedCurrency;
  }

  function bindCurrencySelector() {
    document.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.matches(SELECTOR)) {
        setSelectedCurrency(event.target.value);
      }
    });
  }

  function scheduleConversion() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => applyCurrencyConversion(), 40);
  }

  function captureThemePriceUpdate(priceElement) {
    if (!priceElement) return;
    const themePrice = priceElement.textContent.trim();
    if (/\u20B9|\bINR\b/i.test(themePrice)) priceElement.dataset.basePriceOriginal = themePrice;
  }

  const priceObserver = new MutationObserver((records) => {
    let needsConversion = false;
    records.forEach((record) => {
      const targetElement = record.target.nodeType === Node.TEXT_NODE ? record.target.parentElement : record.target;
      const existingPrice = targetElement?.closest?.(PRICE_SELECTOR);
      if (existingPrice) {
        captureThemePriceUpdate(existingPrice);
        needsConversion = true;
      }
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(PRICE_SELECTOR) || node.querySelector(PRICE_SELECTOR)) needsConversion = true;
      });
    });
    if (needsConversion) scheduleConversion();
  });

  function initializeCurrencyConverter() {
    selectedCurrency = getSelectedCurrency();
    applyCurrencyConversion();
    if (!observerStarted && document.body) {
      observerStarted = true;
      priceObserver.observe(document.body, { childList: true, characterData: true, subtree: true });
    }
  }

  bindCurrencySelector();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCurrencyConverter, { once: true });
  } else {
    initializeCurrencyConverter();
  }

  window.addEventListener('load', initializeCurrencyConverter);
  window.addEventListener('pageshow', initializeCurrencyConverter);
  document.addEventListener('variant:update', scheduleConversion);
  document.addEventListener('variant:change', scheduleConversion);
  document.addEventListener('shopify:section:load', scheduleConversion);
  document.addEventListener('section:cached', scheduleConversion);
  document.addEventListener('product-info:loaded', scheduleConversion);
  document.addEventListener('productsLoaded', scheduleConversion);

  window.RRDisplayCurrency = Object.freeze({
    formatMoney,
    getSelectedCurrency,
    setSelectedCurrency,
    applyCurrencyConversion,
    bindCurrencySelector,
    currencies: CURRENCIES,
    baseCurrency: BASE_CURRENCY
  });
})();
